import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import LegalLayout, { H2, H3, P, UL, Note } from "./LegalLayout";
import api from "@/lib/api";
import { renderMarkdown } from "@/lib/landing";

const TOC = [
  { id: "introduction",       label: "1. Introduction" },
  { id: "information",        label: "2. Information We Collect" },
  { id: "how-we-use",         label: "3. How We Use Information" },
  { id: "sharing",            label: "4. Sharing & Disclosure" },
  { id: "children",           label: "5. Children's Data" },
  { id: "security",           label: "6. Data Security" },
  { id: "retention",          label: "7. Data Retention" },
  { id: "your-rights",        label: "8. Your Rights" },
  { id: "cookies",            label: "9. Cookies" },
  { id: "international",      label: "10. International Transfers" },
  { id: "changes",            label: "11. Changes to this Policy" },
  { id: "contact",            label: "12. Contact" },
];

export default function PrivacyPolicyPage() {
  const [override, setOverride] = useState(null);
  useEffect(() => {
    api.get("/site-content").then(({ data }) => {
      if (data?.privacy_policy_md && data.privacy_policy_md.trim()) setOverride(data.privacy_policy_md);
    }).catch(() => {});
  }, []);
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How SchoolBoss collects, uses, and protects information entrusted to us by schools, staff, students and parents."
      updatedAt="February 2026"
      toc={override ? null : TOC}
    >
      {override ? (
        <div className="prose prose-slate max-w-none [&_h1]:font-heading [&_h1]:text-3xl [&_h1]:font-bold [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-1.5 [&_a]:text-[#2563EB] [&_a]:font-semibold hover:[&_a]:underline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(override), { USE_PROFILES: { html: true } }) }} />
      ) : (<>
      <H2 id="introduction">1. Introduction</H2>
      <P>
        SchoolBoss ("we", "us", "our" or "SchoolBoss.Ph") provides a school management platform used by school owners, branch administrators, staff and parents/guardians. This Privacy Policy describes what personal information we collect through the SchoolBoss platform, how we use it, and the rights you have over your information. It applies to the website at <span className="font-mono text-[13px]">schoolboss.ph</span>, every school-branded portal (<span className="font-mono text-[13px]">schoolboss.ph/school/&#123;slug&#125;</span>), and any related services.
      </P>
      <P>
        SchoolBoss is operated from the Philippines and is committed to complying with the Republic Act No. 10173 (Data Privacy Act of 2012) and its implementing rules and regulations.
      </P>

      <Note>
        <strong>Role-clarity:</strong> When a school registers and uploads data about its students, employees and guardians, the school acts as the <em>Personal Information Controller</em>. SchoolBoss acts as the <em>Personal Information Processor</em> on behalf of the school. The school is responsible for obtaining the necessary consents from the people it stores data about.
      </Note>

      <H2 id="information">2. Information We Collect</H2>
      <H3>2.1 Account information</H3>
      <UL>
        <li>Name, email address, and hashed password of school owners and administrators</li>
        <li>School metadata (name, address, contact details, logo, URL slug)</li>
        <li>Branch metadata (name, address, principal, contact details, logo)</li>
      </UL>
      <H3>2.2 Information uploaded by the school</H3>
      <UL>
        <li>Student records — name, photo, birthday, address, enrolled class & section, status</li>
        <li>Guardian information — name, contact details, photo, and whether parent-portal login is enabled</li>
        <li>Employee records — name, photo, role, contact details, salary configuration</li>
        <li>Attendance, grades, progress feedback notes, report cards</li>
        <li>Financial entries — tuition payments, income, expenses, fund transfers, pay slips</li>
      </UL>
      <H3>2.3 Technical information</H3>
      <UL>
        <li>Authentication tokens stored in your browser's local storage</li>
        <li>Server-side activity audit logs (who did what and when)</li>
        <li>Standard web request metadata (IP address, user agent, timestamps)</li>
      </UL>

      <H2 id="how-we-use">3. How We Use Information</H2>
      <P>We use the information described above to:</P>
      <UL>
        <li>Provide the SchoolBoss platform — display dashboards, run reports, render parent portals</li>
        <li>Authenticate users and maintain secure sessions</li>
        <li>Send transactional emails (account approval, password resets, payment confirmations)</li>
        <li>Maintain an audit trail so school owners can see who changed what</li>
        <li>Detect, investigate and prevent abuse, fraud, or breaches</li>
        <li>Improve the platform — diagnose bugs, monitor performance, plan new features</li>
        <li>Comply with applicable laws and respond to lawful government requests</li>
      </UL>

      <H2 id="sharing">4. Sharing &amp; Disclosure</H2>
      <P>SchoolBoss does <strong>not</strong> sell personal information. We share information only in the following situations:</P>
      <UL>
        <li><strong>Within your school:</strong> data uploaded to your school is visible to other authorized users of the same school (owners, branch admins, staff with appropriate permissions, parents whose access has been enabled).</li>
        <li><strong>Service providers:</strong> hosting, database, email and SMS providers strictly bound by data-processing agreements.</li>
        <li><strong>Legal compliance:</strong> when we believe in good faith that disclosure is required by law, court order, or to protect the safety of a user.</li>
        <li><strong>Business transfers:</strong> if SchoolBoss is acquired or merged, your information may transfer as part of that transaction. We will notify you before any such transfer.</li>
      </UL>

      <H2 id="children">5. Children's Data</H2>
      <P>
        Schools using SchoolBoss store information about children, including names, photos and dates of birth. This data is uploaded and managed by the school. Parents and guardians should direct questions about their child's information to the school in the first instance. SchoolBoss will cooperate with the school to fulfill access, correction, or deletion requests within a reasonable timeframe.
      </P>
      <Note>
        Parent-portal accounts use the child's birthday in DDMMYYYY as a temporary password. Parents are required to set a new password on first login. We strongly recommend parents choose a strong, unique password.
      </Note>

      <H2 id="security">6. Data Security</H2>
      <P>We take reasonable administrative, technical and physical safeguards to protect your information, including:</P>
      <UL>
        <li>Encryption in transit (HTTPS/TLS) for all client–server communication</li>
        <li>Password hashing using industry-standard algorithms — we never store plaintext passwords</li>
        <li>Role-based access control (RBAC) enforced on every request</li>
        <li>Activity audit logs reviewable by school owners</li>
        <li>Image uploads are auto-compressed before storage to reduce exposure of large files</li>
        <li>Regular dependency updates and security reviews</li>
      </UL>
      <P>
        No method of transmission or storage is 100% secure. We will notify affected schools and the National Privacy Commission as required by law in the event of a personal-data breach that may put rights and freedoms at risk.
      </P>

      <H2 id="retention">7. Data Retention</H2>
      <UL>
        <li>School records remain on the platform for as long as the school account is active and for up to 90 days after cancellation, to allow recovery.</li>
        <li>After 90 days from cancellation, the school's data is permanently deleted from active databases.</li>
        <li>Backups containing previously-deleted data are rotated out within 12 months.</li>
        <li>Aggregated, non-identifiable analytics may be retained indefinitely.</li>
      </UL>

      <H2 id="your-rights">8. Your Rights</H2>
      <P>Subject to the Data Privacy Act of 2012, you have the right to:</P>
      <UL>
        <li>Be informed of how your data is processed</li>
        <li>Access your data and obtain a copy</li>
        <li>Request correction of inaccurate data</li>
        <li>Object to certain processing</li>
        <li>Request erasure or blocking of your data</li>
        <li>Be indemnified for damages caused by inaccurate, false, unlawfully obtained or unauthorized use of personal data</li>
        <li>Lodge a complaint with the National Privacy Commission</li>
      </UL>
      <P>
        To exercise any of these rights, contact the school that uploaded your data first. If you cannot reach a resolution, email us at <span className="font-mono text-[13px]">privacy@schoolboss.ph</span>.
      </P>

      <H2 id="cookies">9. Cookies &amp; Local Storage</H2>
      <P>
        SchoolBoss uses local storage (not cookies) to keep you signed in across page reloads. We do not use third-party advertising or cross-site tracking cookies. Clearing your browser's local storage will sign you out.
      </P>

      <H2 id="international">10. International Transfers</H2>
      <P>
        Some of our infrastructure providers operate data centers outside the Philippines. When personal information is transferred internationally, we put in place contractual safeguards consistent with the Data Privacy Act of 2012.
      </P>

      <H2 id="changes">11. Changes to this Policy</H2>
      <P>
        We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top. Material changes will be announced in-app at least 14 days before they take effect.
      </P>

      <H2 id="contact">12. Contact</H2>
      <P>
        Data Protection Officer<br />
        SchoolBoss.Ph<br />
        Governors Drive, Sampaloc 1, Dasmariñas, Philippines, 4114<br />
        Email: <span className="font-mono text-[13px]">privacy@schoolboss.ph</span><br />
        Phone: +63 908 908 4776
      </P>
      </>)}
    </LegalLayout>
  );
}
